import { useQuery } from "@tanstack/react-query";

import type { IconName } from "@similarweb/icons";

import { US_COUNTRY_ID } from "app/services/countryServiceConst";
import { DefaultFetchService } from "app/services/fetchService";

const ROUTE = "api/MarketResearch/DemandSearchTrends/Topics";

export type Topic = {
    Id: string;
    Name: string;
    Level: number;
    Path: string;
    Children?: Record<string, Topic>;
    Favicon?: IconName;
};

export type TopicsRawData = Record<string, Topic>;

type DemandSearchTrendsTopicsParams = {
    country: number;
};

const getTopicsData = async (queryParams: DemandSearchTrendsTopicsParams): Promise<TopicsRawData> => {
    const fetchService = DefaultFetchService.getInstance();
    return fetchService.get<TopicsRawData>(ROUTE, queryParams);
};

type ProcessedTopicsData = {
    rawData: TopicsRawData;
    topicsById: Map<string, Topic>;
    flattenedTopics: Topic[];
};

const select = (rawData: TopicsRawData): ProcessedTopicsData => {
    const topicsById = new Map<string, Topic>();
    const flattenedTopics: Topic[] = [];

    const processTopics = (topic: Topic): void => {
        if (!topicsById.has(topic.Id)) {
            topicsById.set(topic.Id, topic);
            flattenedTopics.push(topic);
        }

        const children = topic.Children;
        if (children) {
            Object.values(children).forEach(processTopics);
        }
    };

    Object.values(rawData).forEach(processTopics);

    return {
        rawData,
        topicsById,
        flattenedTopics,
    };
};

export const useTopicsData = (queryParams: DemandSearchTrendsTopicsParams = { country: US_COUNTRY_ID }) => {
    return useQuery({
        queryKey: [ROUTE],
        queryFn: async () => getTopicsData(queryParams),
        select,
    });
};
